import {
  dmMono,
  labelStyle,
  textInputStyle,
  textAreaStyle,
  secondaryButtonStyle,
  primaryButtonStyle,
  sectionHeaderStyle,
} from './tokens';

function FieldLabel({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: 4, minWidth: 0 }}>
      <span style={labelStyle}>{label}</span>
      {children}
    </label>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={{ marginBottom: 10, minWidth: 0 }}>
      <div style={{ ...labelStyle, marginBottom: 3 }}>{label}</div>
      <div
        style={{
          ...dmMono,
          fontSize: 11,
          color: 'rgba(255,255,255,0.82)',
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          maxWidth: '100%',
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {value ? value : <span style={{ color: 'rgba(255,255,255,0.25)' }}>Not set</span>}
      </div>
    </div>
  );
}

export default function PersonaSection({
  mode = 'view',
  values,
  onChange,
  onEnterEdit,
  onSave,
  onCancel,
  saving = false,
  color = '#53b1fd',
  title = 'Persona',
  showHeader = true,
  showActions = true,
  modelLabel = null,
  forceEdit = false,
}) {
  const isEditing = forceEdit || mode === 'edit';
  const handle = (field) => (e) => {
    if (onChange) onChange({ ...values, [field]: e.target.value });
  };

  const safeValues = values || {};

  return (
    <div style={{ minWidth: 0 }}>
      {showHeader && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 10,
          }}
        >
          <div style={sectionHeaderStyle}>{title}</div>
          {showActions && !forceEdit && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {isEditing ? (
                <>
                  <button onClick={onCancel} style={secondaryButtonStyle}>
                    Cancel
                  </button>
                  <button
                    onClick={onSave}
                    disabled={saving}
                    style={{
                      ...primaryButtonStyle,
                      opacity: saving ? 0.6 : 1,
                      cursor: saving ? 'progress' : 'pointer',
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </>
              ) : (
                <button onClick={onEnterEdit} style={secondaryButtonStyle}>
                  Edit
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {isEditing ? (
        <div
          style={{
            display: 'grid',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 8,
            border: `1px solid ${color}33`,
            background: 'rgba(255,255,255,0.02)',
            minWidth: 0,
          }}
        >
          <FieldLabel label="Name">
            <input
              value={safeValues.name || ''}
              onChange={handle('name')}
              style={textInputStyle}
              placeholder="What should we call them?"
            />
          </FieldLabel>
          <FieldLabel label="Personality">
            <textarea
              value={safeValues.personality_prompt || ''}
              onChange={handle('personality_prompt')}
              rows={3}
              style={textAreaStyle}
              placeholder="How do they think, feel, and speak?"
            />
          </FieldLabel>
          <FieldLabel label="Backstory">
            <textarea
              value={safeValues.backstory_prompt || ''}
              onChange={handle('backstory_prompt')}
              rows={3}
              style={textAreaStyle}
              placeholder="Where did they come from? What do they spend their days doing?"
            />
          </FieldLabel>
          <FieldLabel label="Quirks & Mannerisms">
            <textarea
              value={safeValues.quirks_and_mannerisms || ''}
              onChange={handle('quirks_and_mannerisms')}
              rows={3}
              style={textAreaStyle}
              placeholder="Habits, tics, conversational tells..."
            />
          </FieldLabel>
          <FieldLabel label="Beliefs & Intents">
            <textarea
              value={safeValues.beliefs_and_intents || ''}
              onChange={handle('beliefs_and_intents')}
              rows={3}
              style={textAreaStyle}
              placeholder="Core beliefs, guiding principles, intentions..."
            />
          </FieldLabel>
          <FieldLabel label="Verbosity">
            <select
              value={safeValues.verbosity || 'medium'}
              onChange={handle('verbosity')}
              style={textInputStyle}
            >
              <option value="low">Concise</option>
              <option value="medium">Medium</option>
              <option value="high">Verbose</option>
            </select>
          </FieldLabel>
        </div>
      ) : (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.02)',
            minWidth: 0,
          }}
        >
          {modelLabel && <SummaryRow label="Chat Model" value={modelLabel} />}
          <SummaryRow label="Name" value={safeValues.name} />
          <SummaryRow label="Personality" value={safeValues.personality_prompt} />
          <SummaryRow label="Backstory" value={safeValues.backstory_prompt} />
          <SummaryRow label="Quirks" value={safeValues.quirks_and_mannerisms} />
          <SummaryRow label="Beliefs" value={safeValues.beliefs_and_intents} />
          <SummaryRow
            label="Verbosity"
            value={
              safeValues.verbosity
                ? `${safeValues.verbosity} (${safeValues.response_max_characters || 400} chars)`
                : null
            }
          />
        </div>
      )}
    </div>
  );
}
